import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Story } from '../../../../dorian-shared/types/story/Story';
import { User } from '../../../../dorian-shared/types/user/User';
import { AuditStore, createAuditStore } from '../audit/auditStore';
import { canSettle, withSettlement } from '../audit/auditRecord';
import { toJsonlBlob } from '../audit/exportJsonl';
import { api } from '../../../api';
import { BasilEnvironment, detectBasilEnvironment } from '../../../utils/basilGate';
import {
  ApplyDeps,
  beginSettlement,
  endSettlement,
  isActiveWrite,
  runApply,
} from './applyController';
import { basilWriteRefusal, BasilWriteSubject } from './canBasilWrite';
import {
  ApplyRefusal,
  ApplyState,
  BasilCanonSet,
  BasilHttp,
  BasilPlan,
  BasilVerifyClient,
  BranchesRead,
  EMPTY_APPLY_STATE,
} from './writeTypes';

/**
 * The apply hook (spec section 8.4).
 *
 * It owns nothing but the seam: the gate, the pipeline, the transports and the audit store are
 * plain functions, tested without React. The hook wires the page's axios instance, the detected
 * engine environment (amendment A1) and the durable audit store into {@link runApply}.
 *
 * Two things it is careful about, both of which used to be wrong:
 *
 * 1. **Apply enables only after a handshake has ANSWERED.** `verifierAvailable` is a value the
 *    surface re-renders on (`useVerifyClient`), not a getter on a stable client object — a mutable
 *    field can flip without React ever hearing about it, which left Apply disabled for the life of
 *    the modal.
 * 2. **The staleness baseline is passed in, and it is the verifier's.** `baselineCanon` is the
 *    canon the read lane took at decompile time over the worker boundary. Without it the pipeline
 *    has nothing to prove the baseline with and refuses; the hook never substitutes a projection of
 *    its own, because the verifier's canonicalisation lives in the worker chunk and nowhere else.
 * 3. **What is applied is frozen by the caller, not read when the dialog resolves.** `apply` used
 *    to take its plans and its baseline from `argsRef.current` at the moment the operator
 *    confirmed, so an analysis that completed while the confirmation was open silently replaced the
 *    reviewed change set. The surface calls {@link UseApplyResult.freeze} when the dialog OPENS and
 *    hands that same object back to `apply`; nothing about the change set is read afterwards.
 * 4. **`apply` resolves with the final {@link ApplyState}.** It used to drop it and always resolve,
 *    which let the surface treat a refusal as a success and close over the writer's unsaved script.
 */

export interface UseApplyArgs {
  episodeId: number;
  story: Pick<Story, 'id' | 'group'> | null;
  user: Pick<User, 'id'> | null;
  plans: BasilPlan[];
  /** the read the script was decompiled from. */
  before: BranchesRead | null;
  /** the verifier's canon of that read (`useEpisodeScript().canon`); the staleness baseline. */
  baselineCanon: BasilCanonSet | null;
  verifyClient: BasilVerifyClient;
  /** `useVerifyClient().available` — true only once the handshake has answered. */
  verifierAvailable: boolean;
  isReadOnly: boolean;
  bookId?: number | null;
  /** test seams; both default to the real page instances. */
  http?: BasilHttp;
  auditStore?: AuditStore;
  environment?: BasilEnvironment;
}

/**
 * The change set an apply runs, captured when the operator asked for the confirmation.
 *
 * It is a value, not a reference into the hook's live arguments: the debounced analysis keeps
 * running while a confirmation dialog is open, and what is sent must be what was reviewed.
 */
export interface FrozenApplyArgs {
  plans: BasilPlan[];
  /** the read the frozen plans were built against. */
  before: BranchesRead | null;
  /** the verifier's canon of that read. */
  baselineCanon: BasilCanonSet | null;
}

export interface UseApplyResult {
  canApply: boolean;
  reason: ApplyRefusal | null;
  state: ApplyState;
  /** Capture the change set to be confirmed. Call it when the dialog OPENS. */
  freeze: () => FrozenApplyArgs;
  /** Run the frozen change set and resolve with the pipeline's own final state. */
  apply: (frozen: FrozenApplyArgs) => Promise<ApplyState>;
  abort: () => void;
  exportAudit: () => Promise<Blob>;
  /**
   * How many entries of THIS episode the durable log is still waiting on (S-3).
   *
   * Read by the surface when the editor opens, so a writer whose earlier session left an unknown
   * outcome behind sees the warning and the way out BEFORE spending an apply on a refusal.
   */
  countEarlierUnresolved: () => Promise<number>;
  /**
   * Write the writer's attestation over every unresolved entry of this episode (S-3).
   *
   * Rejects if any write fails, and the surface keeps its warning when it does. It also rejects
   * when an apply is in flight (S-3b): the two write the same rows, so they take the write lane in
   * turn. `remaining` is read back from the log after the sweep, so it is what the guard will
   * actually raise rather than what the sweep believed it left.
   */
  settleEarlier: () => Promise<{ settled: number; remaining: number }>;
}

function readBaseUrl(instance: unknown): string {
  const { defaults } = instance as { defaults?: { baseURL?: string } };

  return (defaults && defaults.baseURL) || '';
}

export function useApply(args: UseApplyArgs): UseApplyResult {
  const [state, setState] = useState<ApplyState>(EMPTY_APPLY_STATE);
  const abortRef = useRef(false);
  const argsRef = useRef(args);

  // (F1) mirror every value the async apply reads, as SelectiveEditModal.tsx:105-129 does.
  argsRef.current = args;

  const environment = args.environment || detectBasilEnvironment(readBaseUrl(api));
  const auditStore = useMemo(
    () => args.auditStore || createAuditStore(),
    [args.auditStore],
  );

  const reason = useMemo<ApplyRefusal | null>(() => {
    const gate = basilWriteRefusal({
      user: args.user, story: args.story, environment, isReadOnly: args.isReadOnly,
    });

    if (gate) {
      return gate;
    }

    if (!args.verifierAvailable || !args.verifyClient || args.verifyClient.available === false) {
      return 'verifier-unavailable';
    }

    if (args.plans.length === 0) {
      return 'nothing-to-apply';
    }

    return null;
  }, [
    args.user,
    args.story,
    args.isReadOnly,
    args.verifyClient,
    args.verifierAvailable,
    args.plans,
    environment,
  ]);

  /** The subject as the page holds it now: re-read by the controller before every chunk. */
  const readSubject = useCallback((): BasilWriteSubject => {
    const { current } = argsRef;

    return {
      user: current.user,
      story: current.story,
      environment: current.environment || detectBasilEnvironment(readBaseUrl(api)),
      isReadOnly: current.isReadOnly,
    };
  }, []);

  /** The change set as it stands NOW, as a value the caller keeps. */
  const freeze = useCallback((): FrozenApplyArgs => {
    const { current } = argsRef;

    return {
      plans: current.plans.slice(),
      before: current.before,
      baselineCanon: current.baselineCanon,
    };
  }, []);

  const apply = useCallback(async (frozen: FrozenApplyArgs): Promise<ApplyState> => {
    const { current } = argsRef;

    abortRef.current = false;

    const deps: ApplyDeps = {
      http: current.http || (api as unknown as BasilHttp),
      verifyClient: current.verifyClient,
      audit: auditStore,
      readSubject,
      onState: (next: ApplyState) => setState(next),
      shouldAbort: () => abortRef.current,
    };

    // The plans, the read they were built against and its canon come from the FROZEN set — never
    // from `argsRef`, which the debounced analysis is still writing to. The subject deliberately
    // does not: it is re-read before every chunk, because a story that goes live mid-apply must
    // stop the queue.
    const final = await runApply(deps, {
      episodeId: current.episodeId,
      storyId: current.story ? current.story.id : current.episodeId,
      bookId: current.bookId,
      subject: readSubject(),
      plans: frozen.plans,
      baseline: frozen.before,
      baselineCanon: frozen.baselineCanon,
    });

    return final;
  }, [auditStore, readSubject]);

  const abort = useCallback(() => {
    abortRef.current = true;
  }, []);

  const exportAudit = useCallback(async () => {
    const records = await auditStore.listAll();

    return toJsonlBlob(records);
  }, [auditStore]);

  /**
   * The entries of THIS episode the log is still waiting on.
   *
   * The id is the one the write lane records — the story's, falling back to the episode's — built
   * from the same expression `apply` uses, so the two can never read the log by different keys.
   * Another episode's unresolved entries are none of this editor's business and are left alone.
   */
  const mineUnresolved = useCallback(async () => {
    const { current } = argsRef;
    const storyId = current.story ? current.story.id : current.episodeId;
    const rows = await auditStore.listUnresolved();

    return rows.filter((row) => row.story && row.story.storyId === storyId);
  }, [auditStore]);

  const countEarlierUnresolved = useCallback(
    async () => (await mineUnresolved()).length,
    [mineUnresolved],
  );

  const settleEarlier = useCallback(async () => {
    const { current } = argsRef;
    const attestedBy = current.user ? current.user.id : null;

    // S-3b, the lock, taken BEFORE anything is read. Apply and settlement both read the unresolved
    // rows and both write them, so they take the write lane in turn: this refuses while an apply is
    // out, and `runApply` refuses while this is. The surface reports the refusal as a settlement
    // that was not saved, which is what it is, and the writer presses again.
    if (!beginSettlement()) {
      throw new Error('an apply is in flight, so the attestation was not written');
    }

    try {
      const rows = await mineUnresolved();
      // An entry this session's apply is still responsible for is not the writer's to answer: the
      // request may still be on its way out. Only ABANDONED entries reach the transition.
      const settleable = rows.filter(
        (row) => canSettle(row) && !isActiveWrite(row.planId, row.chunkIndex),
      );
      let settled = 0;

      for (let index = 0; index < settleable.length; index += 1) {
        const row = settleable[index];
        // The row is re-read INSIDE the transaction that writes it and the transition is applied to
        // that row, so a row the apply lane closed while this sweep was running is never overwritten
        // by the stale copy this sweep listed. The same two questions are asked again in there,
        // against the current row.
        // eslint-disable-next-line no-await-in-loop -- one durable write at a time, like every other
        const wrote = await auditStore.transitionRecord(row.planId, row.chunkIndex, (latest) => (
          canSettle(latest) && !isActiveWrite(latest.planId, latest.chunkIndex)
            ? withSettlement(latest, { attestedBy })
            : null
        ));

        if (wrote) {
          settled += 1;
        }
      }

      // What the guard will still raise, read back from the log rather than inferred from the sweep.
      const remaining = (await mineUnresolved()).length;

      return { settled, remaining };
    } finally {
      endSettlement();
    }
  }, [auditStore, mineUnresolved]);

  return {
    canApply: reason === null,
    reason,
    state,
    freeze,
    apply,
    abort,
    exportAudit,
    countEarlierUnresolved,
    settleEarlier,
  };
}
